import { UUID } from "shared-ts/classes/uuid"

export const ItemSetLineType = {
  SaleProduct: "SaleProduct",
  SaleKit: "SaleKit",
} as const
export type ItemSetLineType =
  (typeof ItemSetLineType)[keyof typeof ItemSetLineType]

export interface ItemSetLine {
  saleLineId: UUID
  lineType: ItemSetLineType
}

export interface ItemSet {
  id: UUID
  name: string
  position: number
  saleProductIds: UUID[]
  saleKitIds: UUID[]
}

export type ItemSetSubmissionArgs = {
  assignToItemSet: (saleLineId: UUID, lineType: ItemSetLineType) => void
  itemSetId?: UUID
  itemSetIdBySaleLineId: Record<string, string>
}

interface ItemSetState {
  itemSetsById: Record<string, ItemSet>
  selectedItemSetId?: UUID
}

export const ItemSetActionTypes = {
  CREATE: "CREATE",
  RENAME: "RENAME",
  REMOVE: "REMOVE",
  RESET: "RESET",
  SET_SELECTED: "SET_SELECTED",
  SEED: "SEED",
} as const

export const LineItemActionTypes = {
  ADD_LINE_ITEM: "ADD_LINE_ITEM",
  REMOVE_LINE_ITEM: "REMOVE_LINE_ITEM",
  MOVE_LINE_ITEM: "MOVE_LINE_ITEM",
} as const

interface CreateAction {
  type: typeof ItemSetActionTypes.CREATE
  id: UUID
  name: string
  line?: ItemSetLine
}

interface RenameAction {
  type: typeof ItemSetActionTypes.RENAME
  id: UUID
  name: string
}

interface RemoveAction {
  type: typeof ItemSetActionTypes.REMOVE
  id: UUID
}

interface ResetAction {
  type: typeof ItemSetActionTypes.RESET
}

interface SetSelectedAction {
  type: typeof ItemSetActionTypes.SET_SELECTED
  id?: UUID
}

interface SeedAction {
  type: typeof ItemSetActionTypes.SEED
  itemSetsById: Record<string, ItemSet>
}

type ItemSetAction =
  | CreateAction
  | RenameAction
  | RemoveAction
  | ResetAction
  | SetSelectedAction
  | SeedAction

interface AddLineItemAction {
  type: typeof LineItemActionTypes.ADD_LINE_ITEM
  itemSetId: UUID
  line: ItemSetLine
}

interface RemoveLineItemAction {
  type: typeof LineItemActionTypes.REMOVE_LINE_ITEM
  saleLineId: UUID
}

interface MoveLineItemAction {
  type: typeof LineItemActionTypes.MOVE_LINE_ITEM
  line: ItemSetLine
  toItemSetId?: UUID
}

type LineItemAction =
  | AddLineItemAction
  | RemoveLineItemAction
  | MoveLineItemAction
type Action = ItemSetAction | LineItemAction

export function initialItemSetState(
  seed?: Record<string, ItemSet>
): ItemSetState {
  return { itemSetsById: compactPositions({ ...seed }) }
}

// The BE rejects sales whose item sets have gapped or duplicated positions.
function compactPositions(
  itemSetsById: Record<string, ItemSet>
): Record<string, ItemSet> {
  const compacted: Record<string, ItemSet> = {}
  Object.entries(itemSetsById)
    .sort(([, a], [, b]) => a.position - b.position)
    .forEach(([key, set], i) => {
      compacted[key] = { ...set, position: i + 1 }
    })
  return compacted
}

function hasLine(set: ItemSet, saleLineId: UUID): boolean {
  return (
    set.saleProductIds.some((id) => id.eq(saleLineId)) ||
    set.saleKitIds.some((id) => id.eq(saleLineId))
  )
}

function stripLineItem(
  itemSetsById: Record<string, ItemSet>,
  saleLineId: UUID
): Record<string, ItemSet> {
  const next: Record<string, ItemSet> = {}
  for (const [key, set] of Object.entries(itemSetsById)) {
    next[key] = hasLine(set, saleLineId)
      ? {
          ...set,
          saleProductIds: set.saleProductIds.filter((id) => !id.eq(saleLineId)),
          saleKitIds: set.saleKitIds.filter((id) => !id.eq(saleLineId)),
        }
      : set
  }
  return next
}

function appendLine(
  set: ItemSet,
  { saleLineId, lineType }: ItemSetLine
): ItemSet {
  return lineType === ItemSetLineType.SaleKit
    ? { ...set, saleKitIds: [...set.saleKitIds, saleLineId] }
    : { ...set, saleProductIds: [...set.saleProductIds, saleLineId] }
}

function addLineItemToSet(
  itemSetsById: Record<string, ItemSet>,
  itemSetId: UUID,
  line: ItemSetLine
): Record<string, ItemSet> {
  const stripped = stripLineItem(itemSetsById, line.saleLineId)
  const key = itemSetId.toString()
  const set = stripped[key]
  if (!set) {
    return itemSetsById
  }
  return { ...stripped, [key]: appendLine(set, line) }
}

export function reducer(prev: ItemSetState, action: Action): ItemSetState {
  switch (action.type) {
    case ItemSetActionTypes.CREATE: {
      const itemSetsById = action.line
        ? stripLineItem(prev.itemSetsById, action.line.saleLineId)
        : prev.itemSetsById
      const created: ItemSet = {
        id: action.id,
        name: action.name,
        position: Object.keys(itemSetsById).length + 1,
        saleProductIds: [],
        saleKitIds: [],
      }
      return {
        ...prev,
        itemSetsById: {
          ...itemSetsById,
          [action.id.toString()]: action.line
            ? appendLine(created, action.line)
            : created,
        },
        selectedItemSetId: action.id,
      }
    }
    case ItemSetActionTypes.RENAME: {
      const key = action.id.toString()
      const set = prev.itemSetsById[key]
      if (!set || set.name === action.name) {
        return prev
      }
      return {
        ...prev,
        itemSetsById: {
          ...prev.itemSetsById,
          [key]: { ...set, name: action.name },
        },
      }
    }
    case ItemSetActionTypes.REMOVE: {
      const key = action.id.toString()
      if (!prev.itemSetsById[key]) {
        return prev
      }
      const itemSetsById = { ...prev.itemSetsById }
      delete itemSetsById[key]
      return {
        ...prev,
        itemSetsById: compactPositions(itemSetsById),
        selectedItemSetId: UUID.eqFromPB(prev.selectedItemSetId, action.id)
          ? undefined
          : prev.selectedItemSetId,
      }
    }
    case ItemSetActionTypes.RESET:
      return initialItemSetState()
    case ItemSetActionTypes.SET_SELECTED:
      return { ...prev, selectedItemSetId: action.id }
    case LineItemActionTypes.ADD_LINE_ITEM:
      return {
        ...prev,
        itemSetsById: addLineItemToSet(
          prev.itemSetsById,
          action.itemSetId,
          action.line
        ),
      }
    case LineItemActionTypes.REMOVE_LINE_ITEM:
      return {
        ...prev,
        itemSetsById: stripLineItem(prev.itemSetsById, action.saleLineId),
      }
    case LineItemActionTypes.MOVE_LINE_ITEM:
      return {
        ...prev,
        itemSetsById: action.toItemSetId
          ? addLineItemToSet(prev.itemSetsById, action.toItemSetId, action.line)
          : stripLineItem(prev.itemSetsById, action.line.saleLineId),
      }
    case ItemSetActionTypes.SEED:
      return { itemSetsById: compactPositions(action.itemSetsById) }
  }
}
